<template>
  <div>
    <form v-if="localItems.length" qaid="apple-label-form" class="apple-label-form" @submit.prevent="saveAppleLabels">
      <table qaid="apple-label-table" class="apple-label-table b2b-grid b2b-grid--collapse is-compact">
        <!-- Headers -->
        <thead class="b2b-grid__header">
          <tr>
            <th
              v-for="column in getColumns()"
              :key="column.key"
              class="b2b-grid__cell b2b-grid__head"
              :qaid="`apple-label-heading.${column.key}`"
              :v-if="column.key == 'advertisedPrice' ? `${column.isVisible}` : true"
              :class="[column.cssClasses, column.isVisible]"
            >
              {{ column.label }}
            </th>
          </tr>
        </thead>
        <!-- Apple Labels Grid Body/Rows -->
        <tbody>
          <!-- For Each Product Row -->
          <template v-for="(item, itemIndex) in localItems">
            <apple-label-grid-item
              :key="item.sku + '_' + itemIndex"
              :errors="errors"
              :item="item"
              :columns="columns"
              :disable-toggles="disableToggles"
              :item-index="itemIndex"
            />
          </template>
        </tbody>
      </table>

      <div class="buttons buttons--right-for-md">
        <trek-button
          primary
          small
          type="submit"
          :disabled="!isFormDirty || saveInProgress || formHasErrors"
          qaid="apple-label-button.save"
          ><span v-html="bp('textSaveButton')"></span
        ></trek-button>
        <trek-button secondary small qaid="apple-label-button.clear" @click="dialogVisible = true"
          ><span v-html="bp('textClearButton')"></span
        ></trek-button>
      </div>

      <el-dialog
        :visible.sync="dialogVisible"
        :title="getBaseProperty('textConfirmClearChanges')"
        :center="true"
        :close-on-press-escape="false"
        :close-on-click-modal="false"
        :show-close="false"
        :z-index="16000011"
        :close-dialog-tooltip="bp('closeDialogToolTip')"
      >
        <p>{{ getBaseProperty('textAllChangesWillBeLost') }}</p>

        <template slot="footer">
          <div class="buttons buttons--center">
            <trek-button alert qaid="yes-clear-changes-button" @click="clearChanges"
              ><span v-html="bp('textYesClearChanges')"></span
            ></trek-button>
            <trek-button secondary qaid="go-back-button" @click="dialogVisible = false"
              ><span v-html="bp('textNoGoBack')"></span
            ></trek-button>
          </div>
        </template>
      </el-dialog>
    </form>
  </div>
</template>

<script>
import axios from 'axios';
import cloneDeep from 'lodash/cloneDeep';
import {mapState, mapMutations} from 'vuex';
import {currencyToNumber} from '../../utils/util';
import TrekButton from '@/components/TrekButton';
import AppleLabelGridItem from '@/components/checkout/AppleLabelGridItem';
import ElDialog from '@/components/Dialog';

export default {
  components: {AppleLabelGridItem, ElDialog, TrekButton},

  props: {
    items: {
      type: Array,
      default: () => [],
    },
    columns: {
      type: Array,
      default: () => [],
    },
    baseProduct: {
      type: String,
      default: '',
    },
    textClearButton: {
      type: String,
      default: 'Clear',
    },
    textSaveButton: {
      type: String,
      default: 'Save Changes',
    },
    textCannotSave: {
      type: String,
      default: '....',
    },
    vvScope: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      localItems: this.getLocalItems(),
      saveInProgress: false,
      formDirty: false,
      dialogVisible: false,
      keyCounter: 0,
    };
  },

  computed: {
    activeItems() {
      return this.localItems.filter((item) => {
        return item.dirty || item.retailLabelStatus;
      });
    },

    itemsToUpdate() {
      return this.activeItems.map(
        ({
          id,
          sku,
          retailLabelStatus: status,
          retailLabelProductId: partNumber,
          retailLabelDescription: description,
          retailLabelPrice: price,
        }) => {
          return {
            id,
            sku,
            status,
            partNumber,
            description,
            price,
          };
        }
      );
    },

    disableToggles() {
      return !this.cartToggle;
    },

    formHasErrors() {
      return !!this.errors.items.length;
    },

    isFormDirty() {
      const hasDirtyValidatedFields = Object.keys(this.fields).some((key) => this.fields[key].dirty);
      const hasToggledOnRows = this.localItems.some((item) => item.dirty);

      // using && because at least one row should be toggled on (BBD-5332)
      return hasDirtyValidatedFields || hasToggledOnRows;
    },

    ...mapState('backend', ['cartToggle', 'consumerFriendlyMode']),
  },

  watch: {
    items() {
      this.localItems = this.getLocalItems();
    },
    consumerFriendlyMode() {
      this.getColumns();
    },
  },

  mounted() {
    window.addEventListener('beforeunload', this.warnUser);
  },

  methods: {
    getColumns() {
      this.columns.forEach((column) => {
        if (column.key == 'advertisedPrice') {
          column['isVisible'] = '';
          if (this.consumerFriendlyMode) {
            column.isVisible = 'hidden';
          } else {
            column.isVisible = 'table-cell';
          }
        }
      });
      return this.columns;
    },
    getLocalItems() {
      return cloneDeep(this.items).map((item) => {
        this.$set(item, 'initialRetailLabelStatus', item.retailLabelStatus);
        this.$set(item, 'initialRetailLabelPrice', item.retailLabelPrice);

        if (item.retailLabelStatus && item.retailLabelPrice) {
          this.$set(item, 'retailLabelPrice', currencyToNumber(item.retailLabelPrice));
        }
        return item;
      });
    },

    saveAppleLabels() {
      // If the b2bCartToggle has turned off asyncronously,
      // send a toast, and don't attempt to save any data.
      if (!this.cartToggle) {
        this.clearChanges();

        return this.$notify({
          type: 'alert',
          message: this.textCannotSave,
        });
      }

      // When a row's toggle is off, input shows the formatted price (with currency/delimiter)
      // which cannot be sent to BE so we need to convert string to number before sending
      this.localItems
        .filter((item) => !item.retailLabelStatus)
        .map((item) => {
          this.$set(item, 'retailLabelPrice', currencyToNumber(item.retailLabelPrice));
          return item;
        });

      this.saveInProgress = true;

      this.setUseToastInterceptor(true);
      this.$validator.validateAll().then((result) => {
        if (!result) return (this.saveInProgress = false);

        axios
          .patch('/apple-labels/', this.itemsToUpdate)
          .then(({data: {data}}) => {
            data.forEach((item) => {
              let targetRow = this.items.findIndex((localitem) => {
                return localitem.sku == item.sku;
              });
              this.$set(this.localItems[targetRow], 'initialRetailLabelPrice', item.price);
              this.$set(this.localItems[targetRow], 'initialRetailLabelStatus', item.status);
            });
          })
          .catch((error) => {
            console.log(error);
            // TODO: Check if error indicates that toggle has been set to false during session
            // if (...) {
            //   store.commit('backend/setCartToggle', false);
            // }
          })
          .finally(() => {
            // After sending is done, we restore the formatted pricing for rows being toggled off
            this.localItems
              .filter((item) => !item.retailLabelStatus)
              .map((item) => {
                this.$set(item, 'retailLabelPrice', item.initialRetailLabelPrice);
                return item;
              });

            this.saveInProgress = false;
            this.$validator.reset();
            this.resetDirtyItems();
            this.setUseToastInterceptor(false);
          });
      });
    },

    clearErrors(scope) {
      this.$children.forEach((child) => {
        if (child.$options._componentTag === 'apple-label-grid-item') {
          child.errors.clear(scope);
        }
      });
    },

    clearChanges() {
      this.keyCounter++;
      this.localItems = this.getLocalItems();
      this.dialogVisible = false;
      return this.errors.clear();
    },

    warnUser(e) {
      if (this.isFormDirty) {
        // Cancel the event
        e.preventDefault();
        // Chrome requires returnValue to be set
        e.returnValue = '';
      }
    },

    resetDirtyItems() {
      this.localItems.forEach((item) => (item.dirty = false));
    },

    ...mapMutations('global', ['setUseToastInterceptor']),
  },
};
</script>
